/* ==========================================================================
   MD SEARCH DRAWER
   Drives sections/search-drawer.liquid, fetched through the Section Rendering
   API so categories, colour/size facets and their counts are REAL Shopify
   search data rather than something guessed on the client.

   Loaded once (defer) from snippets/header-search.liquid.
   ========================================================================== */
(function () {
  if (window.__mdSearchDrawer) return;
  window.__mdSearchDrawer = true;

  var SECTION_ID = 'search-drawer';
  var DEBOUNCE_MS = 260;
  var MIN_CHARS = 2;
  var RECENT_KEY = 'md_recent_searches';
  var RECENT_MAX = 6;

  var container = null;
  var timer = null;
  var token = 0;
  var loadedOnce = false;

  // What the shopper actually typed, plus the Lebanon-only availability choice.
  // Kept here rather than read back off the DOM, because the availability tag is
  // smuggled into the query and must never appear in the input.
  //
  // raw vs term (fixed 2 Aug 2026): term is trimmed because it builds the query,
  // but raw is EXACTLY what the shopper typed and is what gets written back into
  // the field. Previously syncState() restored the trimmed term, so typing a
  // space at the end of a word had it deleted again a moment later, once the
  // debounced fetch returned and the panel was re-rendered. That made it
  // impossible to type a second word.
  var state = { term: '', avail: null, raw: '' };

  /* --- query building ------------------------------------------------------
     Shopify's /search does not prefix-match: q=cy finds nothing even though
     "Cyra Maxi Dress" exists (options[prefix]=last does not fix it either).
     A trailing wildcard does: q=cy* -> Cyra. So every word gets a *, which
     makes "maxi dre" search as "maxi* dre*".

     Availability (Lebanon only) rides along as an extra search token, because
     filter.p.tag is ignored on this store and Liquid cannot read custom query
     params. Tags are indexed, and the section only keeps products whose title /
     type / tags contain every query word - so "dress* ONLINE" yields in-stock
     only, and "dress* preorder" yields preorder only. Both verified.
  -------------------------------------------------------------------------- */
  function wildcard(term) {
    return (term || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(function (w) { return w.replace(/\*+$/, '') + '*'; })
      .join(' ');
  }

  function buildQuery() {
    var parts = [];
    var w = wildcard(state.term);
    if (w) parts.push(w);
    if (state.avail) parts.push(state.avail);
    return parts.join(' ');
  }

  function searchPath() {
    return '/search?q=' + encodeURIComponent(buildQuery());
  }

  /* --- recent searches -----------------------------------------------------
     Only DELIBERATE searches are recorded - pressing Enter, tapping a popular
     chip, or opening a result. Recording on every debounced keystroke filled
     the list with "cy", "cyr", "cyra", "par", "pari", "parisian".
  -------------------------------------------------------------------------- */
  function getRecent() {
    try {
      var v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function setRecent(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* private mode - recent searches just won't persist */ }
  }

  function addRecent(term) {
    term = (term || '').trim();
    if (term.length < MIN_CHARS) return;
    var list = getRecent().filter(function (t) {
      return t.toLowerCase() !== term.toLowerCase();
    });
    list.unshift(term);
    setRecent(list);
  }

  function removeRecent(term) {
    setRecent(getRecent().filter(function (t) { return t !== term; }));
    renderRecent();
  }

  function renderRecent() {
    if (!container) return;
    var list = container.querySelector('[data-mdsd-recent]');
    if (!list) return;

    var items = getRecent();
    if (!items.length) {
      list.innerHTML = '<li style="color:#8a8a8a;font-size:12px;padding:7px 0">' + mdT('search_drawer.no_recent', 'No recent searches yet') + '</li>';
      return;
    }

    list.innerHTML = '';
    items.forEach(function (term) {
      var li = document.createElement('li');

      var go = document.createElement('button');
      go.type = 'button';
      go.className = 'mdsd__recent-term';
      go.textContent = term;
      go.addEventListener('click', function () { runTerm(term); });

      var del = document.createElement('button');
      del.type = 'button';
      del.setAttribute('aria-label', mdT('search_drawer.remove_term', 'Remove {term}').replace('{term}', term));
      del.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      del.addEventListener('click', function (e) {
        e.stopPropagation();
        removeRecent(term);
      });

      li.appendChild(go);
      li.appendChild(del);
      list.appendChild(li);
    });
  }

  /* --- fetching ------------------------------------------------------------ */
  function buildUrl(rawUrl) {
    // Filter links come back from Liquid already pointing at /search with the
    // current query + facets, and may carry the section_id we fetched with,
    // so normalise it to exactly one.
    var url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.set('section_id', SECTION_ID);
    return url.toString();
  }

  // Put the UI back into the state the shopper expects after the section is
  // swapped in: the input shows what they typed (never the availability tag),
  // and the active availability button stays lit.
  //
  // This restores state.raw, NOT state.term. They differ by exactly the
  // whitespace the shopper has typed but not yet followed with a word, and
  // restoring the trimmed version here is what used to eat their space bar.
  function syncState() {
    if (!container) return;

    var input = container.querySelector('[data-mdsd-input]');
    if (input && input.value !== state.raw) input.value = state.raw;

    container.querySelectorAll('[data-mdsd-avail]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.dataset.mdsdAvail === state.avail ? 'true' : 'false');
    });
  }

  /* Swap in the fetched panel WITHOUT replacing the search field.
     Returns true when the live field was kept.

     Blowing away container.innerHTML destroys the input the shopper is typing
     into and builds a new one, so focus, value and caret all have to be put
     back by hand afterwards. The caret was restored to the position captured
     when the debounce fired, which is stale the moment they keep typing during
     the request: the caret jumped backwards mid-word, and a space typed just
     before the response landed appeared to do nothing because the next letter
     went in at the old offset. Keeping the field node means none of that state
     is ever lost - only the results below it are replaced. */
  function swapPanel(html) {
    var liveField = container.querySelector('.mdsd__field');
    var livePanel = liveField && liveField.parentNode;

    if (!liveField || !livePanel || !liveField.querySelector('[data-mdsd-input]')) {
      container.innerHTML = html;
      return false;
    }

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newPanel = doc.querySelector('.mdsd__panel');
    if (!newPanel) {
      container.innerHTML = html;
      return false;
    }

    var newField = newPanel.querySelector('.mdsd__field');
    if (newField) newField.parentNode.removeChild(newField);

    while (liveField.nextSibling) livePanel.removeChild(liveField.nextSibling);
    while (newPanel.firstChild) livePanel.appendChild(document.adoptNode(newPanel.firstChild));

    return true;
  }

  function load(rawUrl, opts) {
    opts = opts || {};
    if (!container) return;

    loadedOnce = true;
    var mine = ++token;
    container.classList.add('is-loading');

    fetch(buildUrl(rawUrl), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Search request failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (mine !== token) return; // a newer keystroke already won
        var keptField = swapPanel(html);
        container.classList.remove('is-loading');
        renderRecent();
        syncState();
        // When the live field survived the swap the caret never moved, so leaving
        // it alone is the whole point - see swapPanel().
        if (!keptField && opts.focus !== false) restoreFocus(opts.caret);
      })
      .catch(function () {
        if (mine !== token) return;
        container.classList.remove('is-loading');
        container.innerHTML =
          '<div class="mdsd__panel"><p class="mdsd__empty">' +
          mdT('search_drawer.unavailable', 'Search is unavailable right now. Please try again.') +
          '</p></div>';
      });
  }

  function restoreFocus(caret) {
    var input = container && container.querySelector('[data-mdsd-input]');
    if (!input) return;
    input.focus();
    var pos = typeof caret === 'number' ? caret : input.value.length;
    try { input.setSelectionRange(pos, pos); } catch (e) { /* type=search quirk */ }
  }

  function runTerm(term) {
    addRecent(term);
    state.term = term;
    state.raw = term;
    load(searchPath());
  }

  function ensureLoaded() {
    if (loadedOnce) return;
    load('/search?q=', { focus: false });
  }

  /* --- events -------------------------------------------------------------- */
  function onInput(e) {
    var input = e.target.closest('[data-mdsd-input]');
    if (!input) return;

    clearTimeout(timer);
    state.raw = input.value;
    state.term = input.value.trim();
    var caret = input.selectionStart;

    timer = setTimeout(function () {
      if (state.term.length === 0) {
        // an availability filter on its own is still a valid search
        load(state.avail ? searchPath() : '/search?q=', { caret: 0 });
        return;
      }
      if (state.term.length < MIN_CHARS) return;
      // deliberately NOT addRecent here - typing is not a search
      load(searchPath(), { caret: caret });
    }, DEBOUNCE_MS);
  }

  function closeDrawer(el) {
    var host = el.closest('header-search');
    if (!host) return;
    if (typeof host.close === 'function') { host.close(); return; }
    var native = host.querySelector('dialog-close-button button');
    if (native) { native.click(); return; }
    host.removeAttribute('open');
  }

  function onClick(e) {
    if (!container) return;

    var closeBtn = e.target.closest('[data-mdsd-close]');
    if (closeBtn && container.contains(closeBtn)) {
      e.preventDefault();
      closeDrawer(closeBtn);
      return;
    }

    // Lebanon-only In Stock / Preorder toggle
    var avail = e.target.closest('[data-mdsd-avail]');
    if (avail && container.contains(avail)) {
      e.preventDefault();
      var picked = avail.dataset.mdsdAvail;
      state.avail = state.avail === picked ? null : picked;
      load(state.term || state.avail ? searchPath() : '/search?q=', { focus: false });
      return;
    }

    // opening a result counts as a real search
    var result = e.target.closest('[data-mdsd-result]');
    if (result && container.contains(result)) {
      addRecent(state.term);
      return; // let the link navigate
    }

    var catLink = e.target.closest('[data-mdsd-filter]');
    if (catLink && container.contains(catLink)) {
      e.preventDefault();
      load(catLink.getAttribute('href'), { focus: false });
      return;
    }

    var toggle = e.target.closest('[data-mdsd-filter-toggle]');
    if (toggle && container.contains(toggle)) {
      e.preventDefault();
      load(toggle.dataset.url, { focus: false });
      return;
    }

    var chip = e.target.closest('[data-mdsd-term]');
    if (chip && container.contains(chip)) {
      e.preventDefault();
      runTerm(chip.dataset.mdsdTerm);
      return;
    }

    var toggleBtn = e.target.closest('[data-mdsd-collapse]');
    if (toggleBtn && container.contains(toggleBtn)) {
      e.preventDefault();
      var panel = toggleBtn.nextElementSibling;
      if (panel) {
        var open = panel.classList.toggle('is-open');
        var sign = toggleBtn.querySelector('span:last-child');
        if (sign) sign.textContent = open ? '−' : '+';
      }
    }
  }

  /* --- dual price slider ---------------------------------------------------- */
  function paintRange(wrap) {
    var min = wrap.querySelector('[data-mdsd-range-min]');
    var max = wrap.querySelector('[data-mdsd-range-max]');
    var fill = wrap.querySelector('[data-mdsd-range-fill]');
    if (!min || !max || !fill) return;

    var lo = Math.min(+min.value, +max.value);
    var hi = Math.max(+min.value, +max.value);
    var span = +wrap.dataset.rangeMax || 1;

    fill.style.left = (lo / span) * 100 + '%';
    fill.style.width = ((hi - lo) / span) * 100 + '%';
  }

  function onRangeInput(e) {
    var input = e.target.closest('[data-mdsd-range-min],[data-mdsd-range-max]');
    if (!input || !container || !container.contains(input)) return;

    var wrap = input.closest('[data-mdsd-range]');
    if (!wrap) return;
    paintRange(wrap);

    clearTimeout(timer);
    timer = setTimeout(function () {
      var minEl = wrap.querySelector('[data-mdsd-range-min]');
      var maxEl = wrap.querySelector('[data-mdsd-range-max]');
      var lo = Math.min(+minEl.value, +maxEl.value);
      var hi = Math.max(+minEl.value, +maxEl.value);

      var url = new URL(window.location.origin + '/search');
      url.searchParams.set('q', buildQuery());
      if (wrap.dataset.minParam) url.searchParams.set(wrap.dataset.minParam, lo);
      if (wrap.dataset.maxParam) url.searchParams.set(wrap.dataset.maxParam, hi);

      load(url.toString(), { focus: false });
    }, 400);
  }

  /* --- boot ----------------------------------------------------------------- */
  function init() {
    container = document.querySelector('[data-mdsd-container]');
    if (!container) return;

    container.addEventListener('input', function (e) {
      if (e.target.closest('[data-mdsd-range-min],[data-mdsd-range-max]')) {
        onRangeInput(e);
      } else {
        onInput(e);
      }
    });

    // First focus pulls the empty state (popular + recent + trust bar). Keeping
    // it out of the page HTML means the drawer costs nothing until it is opened.
    container.addEventListener('focusin', function (e) {
      if (e.target.closest('[data-mdsd-input]')) ensureLoaded();
    });

    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      if (!e.target.closest('[data-mdsd-input]')) return;
      e.preventDefault();
      if (!state.term) return;
      addRecent(state.term);
      window.location.href = searchPath();
    });

    /* The X in the field clears what was typed instead of closing the drawer -
       closing it is what an empty field's X does. Capture phase, because the
       pre-fetch copy of the button lives inside <dialog-close-button>, whose own
       handler would otherwise close the drawer before this one ran. */
    document.addEventListener('click', function (e) {
      var clear = e.target.closest('.mdsd__clear, [data-mdsd-close]');
      if (!clear || !container || !container.contains(clear)) return;

      var input = container.querySelector('[data-mdsd-input]');
      if (!input || !input.value) return; // nothing to clear - let it close

      e.preventDefault();
      e.stopPropagation();

      clearTimeout(timer);
      input.value = '';
      state.term = '';
      state.raw = '';
      input.focus();
      load('/search?q=', { focus: false });
    }, true);

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[aria-controls^="header-search"]');
      if (opener) ensureLoaded();
      onClick(e);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
